/* eslint-disable */
import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import {
  Factory, Truck, ClipboardList, CheckCircle,
  AlertCircle, Circle, Minus, Plus,
  Send, ArrowRight, Package, ShoppingCart,
} from 'lucide-react';

const C = {
  yellow:       '#F7EC71',
  yellowLight:  '#FEFCE8',
  yellowDark:   '#E4AC49',
  orange:       '#E4AC49',
  orangeLight:  '#FDF3DC',
  green:        '#7EA594',
  greenLight:   '#EAF2EF',
  cream:        '#F7F6EF',
  charcoal:     '#2F3A42',
  navy:         '#445366',
  mid:          '#7A8A96',
  border:       '#D8DEE3',
  skyBlue:      '#BCD9E5',
  skyBlueLight: '#EAF4F8',
  skyText:      '#3A8FA3',
  white:        '#FFFFFF',
  red:          '#DC3545',
  redLight:     '#FFF0F0',
  amber:        '#E07B00',
  amberLight:   '#FFF3E0',
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getOperationalDate() {
  const now = new Date();
  if (now.getHours() < 2) now.setDate(now.getDate() - 1);
  return now.toISOString().split('T')[0];
}
function getOperationalDayIndex() {
  const now = new Date();
  if (now.getHours() < 2) now.setDate(now.getDate() - 1);
  return now.getDay();
}
function getTodayLabel() { return DAY_NAMES[getOperationalDayIndex()]; }
function formatDisplayDate(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
}
function getDeliveryDate(opDate) {
  const d = new Date(opDate + 'T12:00:00');
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
}
function formatDeliveryDisplay(opDate) {
  const d = new Date(opDate + 'T12:00:00');
  d.setDate(d.getDate() + 1);
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
}

// ── LocalStorage draft helpers ────────────────────────────────────────
function getDraftKey(venue, date, type) {
  return `stocktake_draft__${venue}__${date}__${type}`;
}
function loadDraft(venue, date, type) {
  try {
    const raw = localStorage.getItem(getDraftKey(venue, date, type));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function saveDraft(venue, date, type, values) {
  try {
    localStorage.setItem(getDraftKey(venue, date, type), JSON.stringify(values));
  } catch {}
}
function clearDraft(venue, date, type) {
  try { localStorage.removeItem(getDraftKey(venue, date, type)); } catch {}
}

function VarianceBadge({ stock, par }) {
  if (stock === null || stock === undefined || stock === '') return <span style={{ color: C.border }}>—</span>;
  const s = parseFloat(stock), gap = par - s;
  if (gap <= 0) return <span style={{ color: C.green, fontWeight: 700 }}>✓</span>;
  if (gap < par * 0.3) return <span style={{ color: C.amber, fontWeight: 700 }}>+{gap % 1 === 0 ? gap : gap.toFixed(1)}</span>;
  return <span style={{ color: C.red, fontWeight: 700 }}>+{gap % 1 === 0 ? gap : gap.toFixed(1)}</span>;
}

const sectionLabel = {
  fontSize: '0.7rem', fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '0.08em',
  color: C.navy, marginBottom: '0.6rem',
};

const categoryPill = (active) => ({
  padding: '0.28rem 0.85rem', borderRadius: '999px',
  border: `1.5px solid ${active ? C.navy : C.border}`,
  background: active ? C.navy : C.white,
  color: active ? C.white : C.mid,
  fontSize: '0.78rem', cursor: 'pointer',
  fontWeight: active ? 700 : 500, transition: 'all 0.15s', whiteSpace: 'nowrap',
});

const TAB_CONFIG = {
  internal: { label: 'Internal', Icon: Factory,       accent: C.skyText, bg: C.skyBlueLight },
  external: { label: 'External', Icon: Truck,         accent: C.green,   bg: C.greenLight   },
  history:  { label: 'History',  Icon: ClipboardList, accent: C.navy,    bg: C.cream        },
};

function SectionPill({ tabKey, isActive, onClick, count, submitted }) {
  const { label, Icon, accent } = TAB_CONFIG[tabKey];
  return (
    <button onClick={onClick} style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', gap: '0.25rem',
      padding: '0.65rem 1.25rem', borderRadius: '14px',
      flex: 1, minWidth: '72px',
      border: `2px solid ${isActive ? accent : C.border}`,
      background: isActive ? accent : C.white,
      color: isActive ? C.white : C.mid,
      cursor: 'pointer', position: 'relative',
      boxShadow: isActive ? `0 3px 10px ${accent}44` : '0 1px 3px rgba(0,0,0,0.04)',
      transition: 'all 0.18s',
    }}>
      {submitted && !isActive && (
        <span style={{ position: 'absolute', top: 5, right: 5, width: 8, height: 8, borderRadius: '50%', background: C.green, border: `1.5px solid ${C.white}` }} />
      )}
      <Icon size={16} strokeWidth={2} style={{ opacity: isActive ? 1 : 0.7 }} />
      <span style={{ fontSize: '0.66rem', fontWeight: isActive ? 700 : 500, whiteSpace: 'nowrap', lineHeight: 1.2 }}>
        {label}
      </span>
      {tabKey !== 'history' && count > 0 && (
        <span style={{ background: isActive ? 'rgba(255,255,255,0.28)' : accent, color: C.white, borderRadius: '999px', padding: '0.05rem 0.42rem', fontSize: '0.62rem', fontWeight: 800, minWidth: '18px', textAlign: 'center', lineHeight: 1.4 }}>{count}</span>
      )}
      {tabKey !== 'history' && submitted && (
        <span style={{ fontSize: '0.6rem', color: isActive ? 'rgba(255,255,255,0.8)' : C.green, fontWeight: 700 }}>✓ done</span>
      )}
    </button>
  );
}

function StepperInput({ value, onChange }) {
  const [editing, setEditing]   = useState(false);
  const [inputVal, setInputVal] = useState('');
  const inputRef                = useRef(null);
  const numVal                  = value !== undefined && value !== '' ? parseFloat(value) : null;
  const hasValue                = numVal !== null;

  useEffect(() => {
    if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [editing]);

  function increment() { onChange(String((numVal ?? 0) + 1)); }
  function decrement() { if ((numVal ?? 0) <= 0) return; onChange(String(Math.max(0, (numVal ?? 0) - 1))); }
  function openEdit()  { setInputVal(hasValue ? String(numVal) : ''); setEditing(true); }
  function commit()    {
    const v = parseFloat(inputVal);
    if (!isNaN(v) && v >= 0) onChange(String(v));
    else if (inputVal === '') onChange('');
    setEditing(false);
  }

  const btnBase = { width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.15s', WebkitTapHighlightColor: 'transparent' };

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <button onClick={decrement} disabled={!hasValue || numVal <= 0} style={{ ...btnBase, borderRadius: '8px 0 0 8px', border: `1.5px solid ${C.border}`, borderRight: 'none', background: hasValue && numVal > 0 ? C.navy : C.cream, color: hasValue && numVal > 0 ? C.white : C.mid, cursor: hasValue && numVal > 0 ? 'pointer' : 'default' }}>
        <Minus size={13} strokeWidth={2.5} />
      </button>
      {editing ? (
        <input ref={inputRef} type='number' inputMode='decimal' pattern='[0-9]*' value={inputVal}
          onChange={e => setInputVal(e.target.value)} onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
          style={{ width: '56px', height: '36px', border: `1.5px solid ${C.navy}`, borderLeft: 'none', borderRight: 'none', textAlign: 'center', fontSize: '0.95rem', fontWeight: 700, color: C.navy, background: C.yellowLight, outline: 'none' }}
        />
      ) : (
        <button onClick={openEdit} style={{ ...btnBase, width: '56px', border: `1.5px solid ${C.border}`, borderLeft: 'none', borderRight: 'none', background: hasValue ? C.yellow : C.white, color: hasValue ? C.charcoal : C.mid }}>
          {hasValue ? numVal : '—'}
        </button>
      )}
      <button onClick={increment} style={{ ...btnBase, borderRadius: '0 8px 8px 0', border: `1.5px solid ${C.border}`, borderLeft: 'none', background: C.navy, color: C.white }}>
        <Plus size={13} strokeWidth={2.5} />
      </button>
    </div>
  );
}

// ── Initials modal ────────────────────────────────────────────────────
function InitialsModal({ onConfirm, onCancel }) {
  const [initials, setInitials] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(44,58,66,0.55)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: C.white, borderRadius: '16px', padding: '2rem', width: '100%', maxWidth: '400px', boxShadow: '0 16px 48px rgba(0,0,0,0.22)' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✅</div>
          <h3 style={{ margin: '0 0 0.35rem', fontSize: '1.15rem', color: C.navy, fontWeight: 800 }}>All counted!</h3>
          <p style={{ margin: 0, fontSize: '0.88rem', color: C.mid }}>Enter your initials to confirm and send to Orders.</p>
        </div>
        <input ref={inputRef} value={initials} onChange={e => setInitials(e.target.value.toUpperCase())}
          onKeyDown={e => e.key === 'Enter' && initials.trim() && onConfirm(initials.trim().toUpperCase())}
          placeholder='e.g. EG' maxLength={4}
          style={{ width: '100%', padding: '0.75rem', textAlign: 'center', border: `2px solid ${initials.trim() ? C.navy : C.border}`, borderRadius: '10px', fontSize: '1.4rem', fontWeight: 800, color: C.navy, outline: 'none', letterSpacing: '0.15em', marginBottom: '1.25rem', boxSizing: 'border-box', transition: 'border-color 0.15s' }}
        />
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button onClick={onCancel} style={{ flex: 1, padding: '0.7rem', border: `1.5px solid ${C.border}`, borderRadius: '8px', background: C.white, cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', color: C.mid }}>Cancel</button>
          <button onClick={() => initials.trim() && onConfirm(initials.trim().toUpperCase())} disabled={!initials.trim()}
            style={{ flex: 2, padding: '0.7rem', background: initials.trim() ? C.yellow : C.cream, color: initials.trim() ? C.charcoal : C.mid, border: 'none', borderRadius: '8px', cursor: initials.trim() ? 'pointer' : 'default', fontWeight: 800, fontSize: '0.95rem', transition: 'all 0.15s' }}>
            Confirm & Go to Orders →
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Post-submit confirmation panel ────────────────────────────────────
function SubmittedPanel({ session, activeTab, orderCount, onNavigateOrders }) {
  const isInternal = activeTab === 'internal';
  const accent = isInternal ? C.skyText : C.green;
  const bg = isInternal ? C.skyBlueLight : C.greenLight;
  const Icon = isInternal ? Factory : Truck;

  return (
    <div style={{ background: bg, border: `1.5px solid ${accent}40`, borderRadius: '12px', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'flex-start' }}>
      {/* Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
        <div style={{ width: 40, height: 40, borderRadius: '10px', background: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <CheckCircle size={22} color={C.white} />
        </div>
        <div>
          <div style={{ fontWeight: 800, fontSize: '1rem', color: C.charcoal }}>
            {isInternal ? 'Internal' : 'External'} stocktake submitted
          </div>
          <div style={{ fontSize: '0.82rem', color: C.mid, marginTop: '0.1rem' }}>
            {session?.submitted_by && <>By <strong>{session.submitted_by}</strong> · </>}
            {session?.submitted_at && new Date(session.submitted_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
            {' · '}{(session?.items || []).length} products counted
          </div>
        </div>
      </div>

      {/* Order created summary */}
      {orderCount > 0 && (
        <div style={{ background: C.white, borderRadius: '8px', padding: '0.75rem 1rem', width: '100%', boxSizing: 'border-box', border: `1px solid ${accent}30` }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.3rem' }}>
            {isInternal ? '🛒 Order created' : '📋 Order references created'}
          </div>
          <div style={{ fontSize: '0.88rem', color: C.charcoal, fontWeight: 600 }}>
            {orderCount} item{orderCount !== 1 ? 's' : ''} below par — {isInternal ? 'order sent to cart' : 'supplier orders pending confirmation'}
          </div>
        </div>
      )}

      {orderCount === 0 && (
        <div style={{ background: C.white, borderRadius: '8px', padding: '0.75rem 1rem', width: '100%', boxSizing: 'border-box', border: `1px solid ${C.green}30` }}>
          <div style={{ fontSize: '0.88rem', color: C.green, fontWeight: 600 }}>✓ All items at or above par — no order needed.</div>
        </div>
      )}

      {/* Instructions */}
      <div style={{ fontSize: '0.83rem', color: C.mid, lineHeight: 1.6 }}>
        {isInternal && orderCount > 0 ? (
          <>To adjust quantities or add items, go to the <strong style={{ color: C.navy }}>Orders page</strong>. Submit the order from there when ready.</>
        ) : isInternal && orderCount === 0 ? (
          <>Stocktake locked. If you need to order something, go to the <strong style={{ color: C.navy }}>Orders page</strong> to place a manual order.</>
        ) : orderCount > 0 ? (
          <>Supplier orders are pending. Go to the <strong style={{ color: C.navy }}>Orders page → External</strong> to review quantities and mark as ordered.</>
        ) : null}
      </div>

      {/* CTA */}
      {onNavigateOrders && (
        <button onClick={onNavigateOrders} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.65rem 1.25rem', background: accent, color: C.white, border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem', transition: 'opacity 0.15s' }}>
          {isInternal ? <><ShoppingCart size={15} /> Go to Orders</> : <><ArrowRight size={15} /> Review External Orders</>}
        </button>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────
export default function Stock({ products, currentUser, requests, setRequests, onNavigate, setPrefilledCart, parMapOverride }) {
  const isAdmin       = currentUser?.role === 'admin';
  const isPrepKitchen = currentUser?.venue === 'Prep Kitchen';
  const userVenue     = currentUser?.venue || '';
  const DEPT_ROLES    = ['kitchen', 'bar'];
  const userDept      = DEPT_ROLES.includes(currentUser?.role) ? currentUser.role : null;

  const opDate       = getOperationalDate();
  const todayLabel   = getTodayLabel();
  const todayIndex   = getOperationalDayIndex();
  const deliveryDate = getDeliveryDate(opDate);

  const defaultTab = isPrepKitchen ? 'external' : 'internal';

  const [activeTab,       setActiveTab]       = useState(defaultTab);
  const [deptFilter,      setDeptFilter]      = useState(userDept || 'all');
  const [categoryFilter,  setCategoryFilter]  = useState('All');
  const [stockValues,     setStockValues]     = useState({});
  const [parMap,          setParMap]          = useState(parMapOverride || {});
  const [parEdits,        setParEdits]        = useState({});
  const [parLoading,      setParLoading]      = useState(!parMapOverride);
  const [saving,          setSaving]          = useState(false);
  const [error,           setError]           = useState(null);

  const [submittedBy,              setSubmittedBy]              = useState('');
  const [submittedInternal,        setSubmittedInternal]        = useState(false);
  const [submittedExternal,        setSubmittedExternal]        = useState(false);
  const [submittedSessionInternal, setSubmittedSessionInternal] = useState(null);
  const [submittedSessionExternal, setSubmittedSessionExternal] = useState(null);
  const [internalOrderCount,       setInternalOrderCount]       = useState(0);
  const [externalOrderCount,       setExternalOrderCount]       = useState(0);
  const [historyLogs,              setHistoryLogs]              = useState([]);
  const [historyLoading,           setHistoryLoading]           = useState(false);
  const [showInitialsModal,        setShowInitialsModal]        = useState(false);

  // Draft save — debounced
  const draftSaveTimer = useRef(null);
  const draftLoaded = useRef(false);

  // Debounced draft save to localStorage
  const scheduleDraftSave = useCallback((values) => {
    if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
    draftSaveTimer.current = setTimeout(() => {
      saveDraft(userVenue, opDate, activeTab, values);
    }, 800);
  }, [userVenue, opDate, activeTab]);

  // Supplier name resolution
  const [localSuppliers, setLocalSuppliers] = useState([]);
  useEffect(() => {
    supabase.from('suppliers').select('id, name').eq('active', true)
      .then(({ data }) => { if (data) setLocalSuppliers(data); });
  }, []);

  // Sort orders
  const [sortOrderMap, setSortOrderMap] = useState({});
  useEffect(() => {
    if (!userVenue) return;
    supabase.from('product_sort_orders').select('product_id, context_type, context_id, position')
      .then(({ data }) => {
        if (!data) return;
        const map = {};
        data.forEach(row => {
          if (!map[row.context_type]) map[row.context_type] = {};
          if (!map[row.context_type][row.context_id]) map[row.context_type][row.context_id] = {};
          map[row.context_type][row.context_id][row.product_id] = row.position;
        });
        setSortOrderMap(map);
      });
  }, [userVenue]);

  const submitted        = activeTab === 'internal' ? submittedInternal : submittedExternal;
  const submittedSession = activeTab === 'internal' ? submittedSessionInternal : submittedSessionExternal;
  const submittedOrderCount = activeTab === 'internal' ? internalOrderCount : externalOrderCount;

  // Load par levels
  useEffect(() => {
    if (!userVenue) return;
    setParLoading(true);
    supabase.from('par_levels').select('*').eq('venue', userVenue).eq('day_of_week', todayIndex)
      .then(({ data }) => {
        if (data) { const m = {}; data.forEach(r => { m[r.product_id] = r.par_level; }); setParMap(m); }
        setParLoading(false);
      });
  }, [userVenue, todayIndex]);

  // Check submitted + load draft on tab switch
  useEffect(() => {
    if (!userVenue) return;
    draftLoaded.current = false;

    ['internal', 'external'].forEach(type => {
      supabase.from('stocktake_log').select('*')
        .eq('venue', userVenue).eq('date', opDate).eq('type', type)
        .order('submitted_at', { ascending: false }).limit(1)
        .then(({ data }) => {
          if (data && data.length > 0) {
            const s = data[0];
            if (type === 'internal') { setSubmittedSessionInternal(s); setSubmittedInternal(true); }
            else                     { setSubmittedSessionExternal(s); setSubmittedExternal(true); }
            const vals = {};
            (s.items || []).forEach(item => {
              if (item.stock !== null && item.stock !== undefined) vals[item.product_id] = String(item.stock);
            });
            setStockValues(prev => ({ ...prev, ...vals }));
            if (s.submitted_by && !submittedBy) setSubmittedBy(s.submitted_by);
            // Clear any stale draft since we have a real submission
            clearDraft(userVenue, opDate, type);
          } else {
            // Not submitted — try to restore draft
            if (type === activeTab && !draftLoaded.current) {
              const draft = loadDraft(userVenue, opDate, type);
              if (draft && Object.keys(draft).length > 0) {
                setStockValues(prev => ({ ...prev, ...draft }));
                draftLoaded.current = true;
              }
            }
          }
        });
    });
  }, [userVenue, opDate]);

  // Restore draft when switching tabs (if not yet submitted)
  useEffect(() => {
    const notSubmitted = activeTab === 'internal' ? !submittedInternal : !submittedExternal;
    if (notSubmitted && userVenue) {
      const draft = loadDraft(userVenue, opDate, activeTab);
      if (draft && Object.keys(draft).length > 0) {
        setStockValues(prev => {
          const merged = { ...prev };
          Object.entries(draft).forEach(([k, v]) => { if (!merged[k]) merged[k] = v; });
          return merged;
        });
      }
    }
  }, [activeTab]);

  // History
  useEffect(() => {
    if (activeTab !== 'history') return;
    setHistoryLoading(true);
    supabase.from('stocktake_log').select('*').eq('venue', userVenue).order('date', { ascending: false }).limit(20)
      .then(({ data }) => { if (data) setHistoryLogs(data); setHistoryLoading(false); });
  }, [activeTab, userVenue]);

  // Product → tab routing
  function productMatchesTab(p, tab) {
    if (!p.active) return false;
    if (!(p.stockTracked || p.stock_tracked)) return false;
    const availableAt = p.availableAt || p.available_at || [];
    if (!availableAt.includes(userVenue)) return false;
    let stDays = p.stocktakeDays || p.stocktake_days || [];
    if (typeof stDays === 'string') { try { stDays = JSON.parse(stDays); } catch { stDays = []; } }
    if (stDays.length > 0 && !stDays.includes(todayLabel)) return false;
    const ft = p.fulfilmentType || p.fulfilment_type || 'external';
    const defaultFulfiller = p.defaultFulfiller || p.default_fulfiller || '';
    if (tab === 'internal') return ft === 'internal' || (ft === 'both' && defaultFulfiller !== userVenue);
    if (tab === 'external') return ft === 'external' || (ft === 'both' && defaultFulfiller === userVenue);
    return false;
  }

  function hasPar(p) {
    const val = parEdits[p.id] ?? parMap[p.id];
    return val !== undefined && val !== null && parseFloat(val) > 0;
  }

  function applyDeptFilter(list) {
    if (deptFilter === 'all') return list;
    return list.filter(p => (p.dept || 'kitchen') === deptFilter);
  }

  const internalProducts = applyDeptFilter(products.filter(p => productMatchesTab(p, 'internal') && (isAdmin || hasPar(p))));
  const externalProducts = applyDeptFilter(products.filter(p => productMatchesTab(p, 'external') && (isAdmin || hasPar(p))));
  const baseProducts     = isPrepKitchen ? externalProducts : (activeTab === 'internal' ? internalProducts : externalProducts);

  const availableCategories = [...new Set(baseProducts.map(p => p.category).filter(Boolean))].sort();

  useEffect(() => {
    if (categoryFilter !== 'All' && !availableCategories.includes(categoryFilter)) setCategoryFilter('All');
  }, [activeTab, availableCategories, categoryFilter]);

  function getSupplierName(product) {
    if (product.supplierName) return product.supplierName;
    const sid = product.supplierId || product.supplier_id;
    if (!sid) return '';
    return localSuppliers.find(s => s.id === sid)?.name || '';
  }

  function getSortPosition(product, tab) {
    if (tab === 'external') {
      const supplierName = getSupplierName(product);
      if (!supplierName) return 9999;
      return sortOrderMap?.supplier?.[supplierName]?.[product.id] ?? 9999;
    }
    if (tab === 'internal') {
      const contextId = `${userVenue}__${product.dept || 'kitchen'}`;
      return sortOrderMap?.stocktake?.[contextId]?.[product.id] ?? 9999;
    }
    return 9999;
  }

  const activeProductsRaw = categoryFilter === 'All' ? baseProducts : baseProducts.filter(p => p.category === categoryFilter);
  const activeProducts = [...activeProductsRaw].sort((a, b) => getSortPosition(a, activeTab) - getSortPosition(b, activeTab));

  function getPar(p)  { return parseFloat(parEdits[p.id] ?? parMap[p.id] ?? 0) || 0; }
  function getGap(p)  {
    const stock = parseFloat(stockValues[p.id]);
    if (isNaN(stock)) return null;
    return Math.max(0, getPar(p) - stock);
  }

  const orderItems  = baseProducts.map(p => ({ product: p, gap: getGap(p) })).filter(({ gap }) => gap !== null && gap > 0);
  const filledCount = baseProducts.filter(p => stockValues[p.id] !== undefined && stockValues[p.id] !== '').length;
  const totalCount  = baseProducts.length;
  const progressPct = totalCount > 0 ? Math.round((filledCount / totalCount) * 100) : 0;
  const allFilled   = totalCount > 0 && filledCount === totalCount;

  // Handle stock value changes — also triggers draft save
  function handleStockChange(productId, val) {
    setStockValues(prev => {
      const next = { ...prev, [productId]: val };
      scheduleDraftSave(next);
      return next;
    });
  }

  // Submit click
  function handleSubmitClick() {
    setError(null);
    const hasAny = baseProducts.some(p => stockValues[p.id] !== undefined && stockValues[p.id] !== '');
    if (!hasAny) { setError('Enter at least one stock value.'); return; }
    setShowInitialsModal(true);
  }

  // Initials confirmed
  async function handleInitialsConfirm(initials) {
    setShowInitialsModal(false);
    setSubmittedBy(initials);
    await doSubmit(initials);
    if (onNavigate) onNavigate('orders');
  }

  // Core submit
  async function doSubmit(initialsOverride) {
    const nameToUse = initialsOverride || submittedBy;
    setSaving(true); setError(null);

    if (isAdmin && Object.keys(parEdits).length > 0) {
      for (const [productId, val] of Object.entries(parEdits)) {
        await supabase.from('par_levels').upsert(
          { product_id: productId, venue: userVenue, day_of_week: todayIndex, par_level: parseFloat(val) || 0 },
          { onConflict: 'product_id,venue,day_of_week' }
        );
      }
      setParMap(prev => {
        const u = { ...prev };
        Object.entries(parEdits).forEach(([id, val]) => { u[id] = parseFloat(val) || 0; });
        return u;
      });
      setParEdits({});
    }

    const items = baseProducts.map(p => ({
      product_id:    p.id,
      product_name:  p.name,
      unit:          p.unit,
      category:      p.category || '',
      dept:          p.dept || '',
      supplier_id:   p.supplierId || p.supplier_id || null,
      supplier_name: getSupplierName(p),
      fulfilment:    p.fulfilmentType || p.fulfilment_type || 'external',
      par:           getPar(p),
      stock:         stockValues[p.id] !== undefined && stockValues[p.id] !== '' ? parseFloat(stockValues[p.id]) : null,
      gap:           getGap(p) ?? 0,
      has_par:       getPar(p) > 0,
    }));

    const logPayload = {
      venue: userVenue, dept: deptFilter === 'all' ? 'all' : deptFilter,
      date: opDate, day_of_week: todayLabel, type: activeTab,
      submitted_by: nameToUse, submitted_at: new Date().toISOString(),
      status: 'submitted', items,
    };

    const currentSession = activeTab === 'internal' ? submittedSessionInternal : submittedSessionExternal;
    let sessionId = currentSession?.id;

    if (currentSession) {
      await supabase.from('stocktake_log').update(logPayload).eq('id', currentSession.id);
    } else {
      const { data, error: e } = await supabase.from('stocktake_log').insert(logPayload).select().single();
      if (e) { setError('Submit failed: ' + e.message); setSaving(false); return; }
      sessionId = data?.id;
      if (activeTab === 'internal') setSubmittedSessionInternal(data);
      else setSubmittedSessionExternal(data);
    }

    if (activeTab === 'internal') setSubmittedInternal(true);
    else setSubmittedExternal(true);

    // Clear draft now that it's submitted
    clearDraft(userVenue, opDate, activeTab);

    const toOrder = items.filter(item => item.gap > 0);
    const orderItemCount = toOrder.length;
    if (activeTab === 'internal') setInternalOrderCount(orderItemCount);
    else setExternalOrderCount(orderItemCount);

    if (toOrder.length > 0) {
      if (activeTab === 'internal') {
        const byFulfiller = {};
        toOrder.forEach(item => {
          const product = products.find(p => p.id === item.product_id);
          const fulfiller = product?.defaultFulfiller || product?.default_fulfiller || 'Prep Kitchen';
          if (!byFulfiller[fulfiller]) byFulfiller[fulfiller] = [];
          byFulfiller[fulfiller].push(item);
        });
        for (const [fulfiller, fItems] of Object.entries(byFulfiller)) {
          const cartItems = fItems.map(item => ({
            id: item.product_id, name: item.product_name, unit: item.unit,
            quantity: item.gap, price: products.find(p => p.id === item.product_id)?.price || 0,
            isFromStocktakeVariance: true, recommendedQty: item.gap,
            dept: products.find(p => p.id === item.product_id)?.dept || item.dept || '',
          }));
          if (fulfiller === 'Prep Kitchen') {
            if (setPrefilledCart) {
              setPrefilledCart({ type: 'internal', orderFrom: fulfiller, venue: userVenue, deliveryDate, items: cartItems, fromStocktake: true });
            }
          } else {
            const { data, error: e } = await supabase.from('requests').insert({
              venue: userVenue, order_from: fulfiller, placed_by: nameToUse,
              date: opDate, delivery_date: deliveryDate, status: 'pending',
              source: 'stocktake_internal', stocktake_log_id: sessionId || null, items: cartItems,
            }).select().single();
            if (!e && data) setRequests(prev => [{ ...data, orderFrom: data.order_from, placedBy: data.placed_by }, ...prev]);
          }
        }
      } else {
        const bySupplier = {};
        toOrder.forEach(item => {
          const product = products.find(p => p.id === item.product_id);
          const supplierName = product ? getSupplierName(product) : '';
          const key = supplierName || 'No Supplier';
          if (!bySupplier[key]) bySupplier[key] = [];
          bySupplier[key].push(item);
        });
        const createdRequests = [];
        for (const [supplierName, sItems] of Object.entries(bySupplier)) {
          const { data, error: e } = await supabase.from('requests').insert({
            venue: userVenue, order_from: supplierName, placed_by: nameToUse,
            date: opDate, delivery_date: deliveryDate, status: 'pending',
            source: 'stocktake_external', stocktake_log_id: sessionId || null,
            items: sItems.map(item => ({
              id: item.product_id, name: item.product_name, unit: item.unit,
              quantity: item.gap, price: products.find(p => p.id === item.product_id)?.price || 0,
              isFromStocktakeVariance: true, recommendedQty: item.gap,
            })),
          }).select().single();
          if (!e && data) createdRequests.push({ ...data, orderFrom: data.order_from, placedBy: data.placed_by });
        }
        if (createdRequests.length > 0) {
          setRequests(prev => [...createdRequests, ...prev]);
          if (setPrefilledCart) setPrefilledCart({ type: 'external', requests: createdRequests, fromStocktake: true });
        }
      }
    }
    setSaving(false);
  }

  const visibleTabs = isPrepKitchen ? ['external', 'history'] : ['internal', 'external', 'history'];

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1200px', margin: '0 auto', minHeight: '100vh', fontFamily: 'system-ui, sans-serif' }}>

      {/* INITIALS MODAL */}
      {showInitialsModal && (
        <InitialsModal
          onConfirm={handleInitialsConfirm}
          onCancel={() => setShowInitialsModal(false)}
        />
      )}

      {/* HEADER */}
      <div style={{ marginBottom: '1.25rem' }}>
        <h2 style={{ margin: '0 0 0.2rem', fontSize: '1.5rem', fontWeight: 800, color: C.navy, letterSpacing: '-0.02em' }}>Stocktake</h2>
        <p style={{ margin: 0, fontSize: '0.88rem', color: C.mid }}>{userVenue} · {formatDisplayDate(opDate)}</p>
      </div>

      {/* TAB PILLS */}
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1.25rem' }}>
        {visibleTabs.map(key => (
          <SectionPill
            key={key} tabKey={key}
            isActive={activeTab === key}
            onClick={() => setActiveTab(key)}
            count={key === 'internal' ? internalProducts.length : key === 'external' ? externalProducts.length : 0}
            submitted={key === 'internal' ? submittedInternal : key === 'external' ? submittedExternal : false}
          />
        ))}
      </div>

      {/* HISTORY TAB */}
      {activeTab === 'history' && (
        <div>
          {historyLoading ? (
            <p style={{ color: C.mid, textAlign: 'center', padding: '2rem' }}>Loading history…</p>
          ) : historyLogs.length === 0 ? (
            <div style={{ background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, padding: '2rem', textAlign: 'center', color: C.mid }}>
              No stocktake history yet.
            </div>
          ) : (
            historyLogs.map(log => (
              <div key={log.id} style={{ background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, marginBottom: '0.75rem', overflow: 'hidden' }}>
                <div style={{ padding: '0.9rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div>
                    <span style={{ fontWeight: 700, color: C.charcoal }}>{formatDisplayDate(log.date)}</span>
                    <span style={{ fontSize: '0.8rem', color: C.mid, marginLeft: '0.75rem' }}>
                      {log.type} · {log.submitted_by} · {(log.items || []).length} items
                    </span>
                  </div>
                  <span style={{ background: C.greenLight, color: C.green, padding: '0.15rem 0.65rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700 }}>Submitted</span>
                </div>
                <div style={{ borderTop: `1px solid ${C.border}`, overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ background: C.cream }}>
                        {['Product', 'Category', 'Unit', 'Par', 'Stock', 'Variance'].map(h => (
                          <th key={h} style={{ padding: '0.4rem 0.75rem', textAlign: 'left', fontSize: '0.7rem', color: C.mid, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(log.items || []).map((item, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                          <td style={{ padding: '0.5rem 0.75rem', fontWeight: 600, color: C.charcoal }}>{item.product_name}</td>
                          <td style={{ padding: '0.5rem 0.75rem', color: C.mid, fontSize: '0.82rem' }}>{item.category || '—'}</td>
                          <td style={{ padding: '0.5rem 0.75rem', color: C.mid }}>{item.unit}</td>
                          <td style={{ padding: '0.5rem 0.75rem', color: C.mid }}>{item.par > 0 ? item.par : '—'}</td>
                          <td style={{ padding: '0.5rem 0.75rem', fontWeight: 600, color: C.charcoal }}>{item.stock ?? '—'}</td>
                          <td style={{ padding: '0.5rem 0.75rem', fontWeight: 700, color: item.gap > 0 ? C.red : C.green }}>
                            {item.gap > 0 ? `+${item.gap}` : item.stock !== null ? '✓' : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* MAIN STOCKTAKE TABS */}
      {activeTab !== 'history' && (
        <>
          {error && (
            <div style={{ background: C.redLight, border: `1px solid ${C.red}40`, borderRadius: '6px', padding: '0.75rem 1rem', marginBottom: '1rem', color: C.red, fontSize: '0.88rem' }}>{error}</div>
          )}

          {/* Draft restore notice */}
          {!submitted && filledCount > 0 && (
            <div style={{ background: C.yellowLight, border: `1px solid ${C.yellowDark}40`, borderRadius: '6px', padding: '0.5rem 0.85rem', marginBottom: '0.85rem', fontSize: '0.78rem', color: C.charcoal, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span>💾</span> Progress saved — {filledCount} of {totalCount} counted. Continue where you left off.
            </div>
          )}

          {/* Context banner */}
          <div style={{
            background: activeTab === 'internal' ? C.skyBlueLight : C.greenLight,
            border: `1px solid ${activeTab === 'internal' ? C.skyBlue : C.green}60`,
            borderRadius: '8px', padding: '0.65rem 1rem', marginBottom: '1.25rem',
            fontSize: '0.85rem', color: activeTab === 'internal' ? C.skyText : C.green,
            fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.5rem',
          }}>
            {activeTab === 'internal'
              ? <><Factory size={14} /> Internal — products fulfilled by Prep Kitchen. Delivery {formatDeliveryDisplay(opDate)}.</>
              : <><Truck size={14} /> External — products ordered from suppliers. Delivery {formatDeliveryDisplay(opDate)}.</>}
          </div>

          {/* Admin dept filter */}
          {isAdmin && (
            <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem' }}>
              {['all', 'kitchen', 'bar'].map(d => (
                <button key={d} onClick={() => setDeptFilter(d)} style={{ ...categoryPill(deptFilter === d), fontSize: '0.8rem' }}>
                  {d === 'all' ? 'All depts' : d.charAt(0).toUpperCase() + d.slice(1)}
                </button>
              ))}
            </div>
          )}

          {/* Category pills */}
          {availableCategories.length > 1 && (
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <button onClick={() => setCategoryFilter('All')} style={categoryPill(categoryFilter === 'All')}>All</button>
              {availableCategories.map(cat => (
                <button key={cat} onClick={() => setCategoryFilter(cat)} style={categoryPill(categoryFilter === cat)}>{cat}</button>
              ))}
            </div>
          )}

          {/* POST-SUBMIT: show confirmation panel */}
          {submitted ? (
            <SubmittedPanel
              session={submittedSession}
              activeTab={activeTab}
              orderCount={submittedOrderCount}
              onNavigateOrders={onNavigate ? () => onNavigate('orders') : null}
            />
          ) : parLoading ? (
            <div style={{ background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, padding: '2.5rem', textAlign: 'center', color: C.mid }}>Loading…</div>
          ) : baseProducts.length === 0 ? (
            <div style={{ background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, padding: '2.5rem', textAlign: 'center', color: C.mid }}>
              <Package size={28} style={{ color: C.border, marginBottom: '0.5rem' }} />
              <div style={{ fontWeight: 600, color: C.navy, marginBottom: '0.25rem' }}>No products have a par level set for {todayLabel}</div>
              <div style={{ fontSize: '0.83rem' }}>
                {isAdmin ? 'Configure par levels in Products.' : 'Ask a manager to configure par levels for today.'}
              </div>
            </div>
          ) : (
            // Active stocktake input
            <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>

              {/* LEFT: table */}
              <div style={{ flex: '1 1 0', minWidth: 0 }}>
                <div style={{ background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: C.cream, borderBottom: `1px solid ${C.border}` }}>
                        <th style={{ padding: '0.7rem 1rem', textAlign: 'left', fontSize: '0.7rem', color: C.mid, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Product</th>
                        <th style={{ padding: '0.7rem 0.75rem', textAlign: 'left', fontSize: '0.7rem', color: C.mid, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Unit</th>
                        {activeTab === 'external' && (
                          <th style={{ padding: '0.7rem 0.75rem', textAlign: 'left', fontSize: '0.7rem', color: C.mid, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Supplier</th>
                        )}
                        <th style={{ padding: '0.7rem 0.75rem', textAlign: 'center', fontSize: '0.7rem', color: C.mid, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Par ({todayLabel})</th>
                        <th style={{ padding: '0.7rem 0.75rem', textAlign: 'center', fontSize: '0.7rem', color: C.mid, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Stock Count</th>
                        <th style={{ padding: '0.7rem 0.75rem', textAlign: 'center', fontSize: '0.7rem', color: C.mid, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Order</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeProducts.map((p, i) => {
                        const par      = getPar(p);
                        const stockVal = stockValues[p.id];
                        const hasVal   = stockVal !== undefined && stockVal !== '';
                        const isLast   = i === activeProducts.length - 1;
                        const supplierName = getSupplierName(p);
                        return (
                          <tr key={p.id} style={{ borderBottom: isLast ? 'none' : `1px solid ${C.border}`, background: hasVal ? C.cream : C.white, transition: 'background 0.15s' }}>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              <div style={{ fontWeight: 600, fontSize: '0.92rem', color: C.charcoal }}>{p.name}</div>
                            </td>
                            <td style={{ padding: '0.75rem 0.75rem' }}>
                              <span style={{ background: C.skyBlueLight, color: C.skyText, borderRadius: '4px', padding: '0.1rem 0.45rem', fontSize: '0.75rem', fontWeight: 700 }}>{p.unit}</span>
                            </td>
                            {activeTab === 'external' && (
                              <td style={{ padding: '0.75rem 0.75rem' }}>
                                <span style={{ background: C.cream, color: C.mid, padding: '0.15rem 0.5rem', borderRadius: '999px', fontSize: '0.75rem' }}>{supplierName || '—'}</span>
                              </td>
                            )}
                            <td style={{ padding: '0.75rem 0.75rem', textAlign: 'center' }}>
                              {isAdmin ? (
                                <input type='number' min='0' step='1'
                                  value={parEdits[p.id] ?? (parMap[p.id] ?? '')}
                                  onChange={e => setParEdits(prev => ({ ...prev, [p.id]: e.target.value }))}
                                  placeholder='—'
                                  style={{ width: '64px', padding: '0.3rem 0.4rem', border: `1.5px solid ${C.yellowDark}`, borderRadius: '6px', textAlign: 'center', fontSize: '0.9rem', background: '#FFFDF0', color: C.charcoal, outline: 'none', fontWeight: 600 }}
                                />
                              ) : (
                                <span style={{ fontWeight: 700, fontSize: '0.92rem', color: par > 0 ? C.navy : C.border }}>
                                  {par > 0 ? par : '—'}
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center' }}>
                              <StepperInput
                                value={stockVal}
                                onChange={val => handleStockChange(p.id, val)}
                              />
                            </td>
                            <td style={{ padding: '0.75rem 0.75rem', textAlign: 'center' }}>
                              <VarianceBadge stock={stockVal} par={par} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* RIGHT: sticky panel */}
              <div style={{ width: '280px', flexShrink: 0, position: 'sticky', top: '1.5rem', alignSelf: 'flex-start', maxHeight: 'calc(100vh - 3rem)', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>

                {/* Progress + submit card */}
                <div style={{ background: C.navy, borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 16px rgba(68,83,102,0.15)' }}>
                  <p style={{ ...sectionLabel, color: C.yellow, marginBottom: '0.75rem' }}>Progress</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#8A9EAE', fontSize: '0.82rem' }}>{filledCount} of {totalCount} counted</span>
                    <span style={{ color: C.yellow, fontWeight: 800, fontSize: '1rem' }}>{progressPct}%</span>
                  </div>
                  <div style={{ background: '#354457', borderRadius: '999px', height: '6px', overflow: 'hidden', marginBottom: '1.25rem' }}>
                    <div style={{ background: progressPct === 100 ? C.green : C.yellow, height: '100%', width: progressPct + '%', borderRadius: '999px', transition: 'width 0.3s ease' }} />
                  </div>

                  <div style={{ background: allFilled ? C.green + '22' : '#354457', border: `1.5px solid ${allFilled ? C.green + '70' : 'transparent'}`, borderRadius: '8px', padding: '0.65rem 0.85rem', marginBottom: '1rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                    {allFilled
                      ? <CheckCircle size={15} style={{ color: C.green, marginTop: '1px', flexShrink: 0 }} />
                      : <Circle      size={15} style={{ color: '#8A9EAE', marginTop: '1px', flexShrink: 0 }} />}
                    <div>
                      <p style={{ margin: 0, fontSize: '0.8rem', fontWeight: 700, color: allFilled ? C.green : '#8A9EAE' }}>
                        {allFilled ? 'All counted — ready to submit!' : 'Count all products'}
                      </p>
                      <p style={{ margin: '0.15rem 0 0', fontSize: '0.72rem', color: '#8A9EAE', lineHeight: 1.4 }}>
                        {allFilled
                          ? 'Hit submit — you\'ll enter your initials to confirm'
                          : `${totalCount - filledCount} item${totalCount - filledCount !== 1 ? 's' : ''} still need a count`}
                      </p>
                    </div>
                  </div>

                  <button onClick={handleSubmitClick} disabled={saving}
                    style={{ width: '100%', padding: '0.75rem', background: saving ? '#354457' : allFilled ? C.yellow : '#4a5a6a', color: saving ? '#5a6a7a' : allFilled ? C.charcoal : '#8A9EAE', border: `1.5px solid ${allFilled ? C.yellow : 'transparent'}`, borderRadius: '8px', cursor: saving ? 'default' : 'pointer', fontWeight: 800, fontSize: '0.95rem', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', boxShadow: allFilled ? `0 0 0 3px ${C.yellow}44` : 'none' }}>
                    <Send size={15} />
                    {saving ? 'Submitting…' : 'Submit Stocktake'}
                  </button>
                </div>

                {/* Live order preview */}
                <div style={{ background: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, padding: '1.25rem' }}>
                  <p style={{ ...sectionLabel, marginBottom: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    {activeTab === 'internal'
                      ? <><Factory size={13} style={{ color: C.navy }} /> Will Order</>
                      : <><Truck   size={13} style={{ color: C.navy }} /> Will Request</>}
                  </p>
                  {orderItems.length === 0 ? (
                    <p style={{ color: C.mid, fontSize: '0.82rem', margin: 0 }}>
                      {filledCount === 0 ? 'Fill in stock counts to see order.' : '✓ All items at or above par'}
                    </p>
                  ) : (
                    <>
                      {orderItems.map(({ product, gap }) => (
                        <div key={product.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.35rem 0', borderBottom: `1px solid ${C.border}`, fontSize: '0.85rem' }}>
                          <span style={{ fontWeight: 600, color: C.charcoal, flex: 1, marginRight: '0.5rem' }}>{product.name}</span>
                          <span style={{ color: C.red, fontWeight: 700, whiteSpace: 'nowrap' }}>+{gap % 1 === 0 ? gap : gap.toFixed(1)}</span>
                        </div>
                      ))}
                      <div style={{ marginTop: '0.6rem', paddingTop: '0.5rem', borderTop: `1px solid ${C.border}` }}>
                        {activeTab === 'internal' ? (
                          <span style={{ background: C.skyBlueLight, color: C.skyText, padding: '0.2rem 0.65rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700 }}>
                            Prep Kitchen · {orderItems.length} item{orderItems.length !== 1 ? 's' : ''} · {formatDeliveryDisplay(opDate)}
                          </span>
                        ) : (
                          (() => {
                            const bySupplier = {};
                            orderItems.forEach(({ product }) => {
                              const s = getSupplierName(product) || 'No Supplier';
                              bySupplier[s] = (bySupplier[s] || 0) + 1;
                            });
                            return Object.entries(bySupplier).map(([s, cnt]) => (
                              <div key={s} style={{ marginBottom: '0.3rem' }}>
                                <span style={{ background: C.greenLight, color: C.green, padding: '0.2rem 0.65rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700 }}>
                                  {s} · {cnt} item{cnt !== 1 ? 's' : ''}
                                </span>
                              </div>
                            ));
                          })()
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}